import React, { useState, useEffect, useCallback } from "react";
import { Mic } from "lucide-react";

const PreviewSpeakRightChallenge = ({ data }) => {
  const targetText = data.text;
  const [isListening, setIsListening] = useState(false);
  const [recognizedText, setRecognizedText] = useState("");
  const [feedback, setFeedback] = useState("");
  const [incorrectWords, setIncorrectWords] = useState([]);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [recognition, setRecognition] = useState(null);

  // Normalize text by removing punctuation and converting to lowercase
  const normalizeText = useCallback((text) => {
    return text.toLowerCase().replace(/[.,]/g, "").trim();
  }, []);

  // Check pronunciation and update feedback
  const checkPronunciation = useCallback(
    (transcript) => {
      const targetWords = normalizeText(targetText).split(" ");
      const spokenWords = normalizeText(transcript)
        .split(" ")
        .filter((word) => word.trim() !== "");

      let incorrectIndexes = [];
      let incorrectCount = 0;

      for (let i = 0; i < spokenWords.length; i++) {
        if (targetWords[i] !== spokenWords[i]) {
          incorrectIndexes.push(i);
          incorrectCount++;
        }
      }

      setIncorrectWords(incorrectIndexes);
      setIncorrectCount(incorrectCount);
      setFeedback(
        incorrectCount === 0
          ? "Correct pronunciation!"
          : `Some words are incorrect. Try again! (Incorrect words: ${incorrectCount})`
      );
    },
    [targetText, normalizeText]
  );

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Your browser does not support Speech Recognition.");
      return;
    }

    const recognitionInstance = new SpeechRecognition();
    recognitionInstance.continuous = true; // Keep listening
    recognitionInstance.interimResults = true; // Show live results
    recognitionInstance.lang = "en-US";

    recognitionInstance.onresult = (event) => {
      let newTranscript = "";
      for (let i = 0; i < event.results.length; i++) {
        newTranscript += event.results[i][0].transcript;
      }
      setRecognizedText(newTranscript.trim());
      checkPronunciation(newTranscript.trim()); // Check pronunciation in real-time
    };

    recognitionInstance.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setFeedback("An error occurred. Please try again.");
      setIsListening(false);
    };

    recognitionInstance.onend = () => {
      if (isListening) {
        recognitionInstance.start(); // Restart if still listening
      }
    };

    setRecognition(recognitionInstance);

    // Cleanup on unmount
    return () => {
      if (recognitionInstance) {
        recognitionInstance.stop();
      }
    };
  }, [isListening, checkPronunciation]);

  // Toggle listening state
  const toggleListening = () => {
    if (!recognition) return;

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      setRecognizedText("");
      setIncorrectWords([]);
      setIncorrectCount(0);
      setFeedback("");
      recognition.start();
      setIsListening(true);
    }
  };

  return (
    <div
      className="font-sans p-6 bg-gray-100 rounded-lg shadow-md text-center relative"
      style={{ paddingBottom: "60px" }}
    >
      <h2 className="text-2xl font-bold mb-4">Paragraph to speak</h2>

      {/* Display the target text */}
      <div
        className="mb-4 p-2 bg-white rounded-lg shadow-sm flex justify-center items-center"
        style={{
          minHeight: "60px",
          padding: "10px",
          wordWrap: "break-word",
          overflowWrap: "break-word",
          textAlign: "center",
        }}
      >
        <p className="text-2xl font-bold text-blue-600">{targetText}</p>
      </div>

      {/* Display recognized text with incorrect words highlighted */}
      <div className="mt-4">
        <p className="text-lg font-medium">You said:</p>
        <p className="text-xl text-gray-700">
          {recognizedText.split(" ").map((word, index) => (
            <span
              key={index}
              className={
                incorrectWords.includes(index)
                  ? "text-red-500 font-bold"
                  : ""
              }
            >
              {word}{" "}
            </span>
          ))}
        </p>
      </div>

      {/* Feedback Message */}
      {feedback && (
        <div className="mt-4 mb-4">
          <p
            className={`text-lg font-bold ${
              feedback.includes("Correct")
                ? "text-green-600"
                : "text-red-600"
            }`}
          >
            {feedback}
          </p>
        </div>
      )}

      {/* Button to start/stop speech recognition */}
      <button
        onClick={toggleListening}
        className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-full mt-2 text-white shadow-lg ${
          isListening ? "bg-red-500" : "bg-blue-500"
        }`}
        style={{ fontSize: "20px" }}
      >
        <Mic />
      </button>
    </div>
  );
};

export default PreviewSpeakRightChallenge;